/**
 * AutoSwapConfirmDialog
 *
 * Shown by useDeployActions.confirmDeploy when a deploy attempt in a
 * local environment finds another site already running. Asks the user
 * to confirm tearing down the running site (docker compose down) before
 * starting the new one — required because all sites in
 * docker/deploy/{site}/docker-compose.yaml share container_names and
 * host ports, so two stacks cannot coexist on a single machine.
 *
 * Hidden in production: the gate isLocalEnvironment() in useAutoSwap
 * skips this flow entirely on remote servers.
 */
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material';

export interface AutoSwapConfirmDialogProps {
  open: boolean;
  otherSite: string | null;
  targetSite: string | null;
  onAccept: () => void;
  onReject: () => void;
}

export function AutoSwapConfirmDialog(props: AutoSwapConfirmDialogProps) {
  const { open, otherSite, targetSite, onAccept, onReject } = props;
  if (!otherSite || !targetSite) return null;

  return (
    <Dialog open={open} onClose={onReject} maxWidth="sm" fullWidth>
      <DialogTitle>다른 사이트가 구동 중입니다</DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          현재 <strong>{otherSite}</strong> 현장이 로컬 서버에서 구동 중입니다.{' '}
          <strong>{targetSite}</strong> 를 배포하려면 먼저 {otherSite} 를 중단해야 합니다.
        </Typography>
        <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
          <Typography variant="body2" component="div">
            ✓ {otherSite} 중단 (docker compose down)
            <br />
            ✓ 중단 완료 대기 (최대 60초)
            <br />
            ✓ {targetSite} 배포 시작
          </Typography>
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 2, display: 'block' }}
        >
          위 작업을 자동으로 진행할까요? 운영 서버에서는 이 다이얼로그가 나타나지 않습니다.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onReject}>취소</Button>
        <Button variant="contained" onClick={onAccept} autoFocus>
          중단 후 배포
        </Button>
      </DialogActions>
    </Dialog>
  );
}
